import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiError } from '../../shared/models/api-error.model';
import { ToastService } from '../../shared/services/toast.service';

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const router = inject(Router);
  const transloco = inject(TranslocoService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const backendMessage = extractBackendMessage(err);
        const apiError: ApiError = {
          status: err.status,
          message: backendMessage ?? err.message,
          details: err.error,
        };

        if (err.status === 401) {
          toastService.showWarning(transloco.translate('errors.sessionExpired'));
          if (!router.url.startsWith('/auth/login')) {
            void router.navigateByUrl('/auth/login');
          }
        } else if (apiError.message) {
          if (err.status >= 400 && err.status < 500) {
            toastService.showWarning(apiError.message);
          } else {
            toastService.showError(apiError.message);
          }
        }

        console.error('API Error mapped:', apiError);
        return throwError(() => apiError);
      }
      return throwError(() => err);
    }),
  );
};

function extractBackendMessage(err: HttpErrorResponse): string | null {
  if (typeof err.error === 'string' && err.error.trim()) {
    return err.error.trim();
  }

  if (err.error && typeof err.error === 'object') {
    for (const key of ['detail', 'message', 'title'] as const) {
      const value = key in err.error ? err.error[key] : null;
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return null;
}
